import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import {
    Container,
    Row,
    Col,
    Button,
    Spinner,
    FormGroup,
    Label,
    Input,
} from 'reactstrap';
import Select from 'react-select';
import Session from './auth/Session';

// -- Date helpers

// Formats a Date as a readable day label: "Wednesday, February 27, 2026"
const formatDayLabel = (date) =>
    date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });

// Adds or subtracts days from a date, returning a new Date.
const shiftDate = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

// Local YYYY-MM-DD - NO UTC conversion. The API expects a DateOnly, and
// toISOString() would shift the date across the UTC boundary in the evening.
const toLocalDateString = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// -- Hours helpers

// "HH:mm" or "HH:mm:ss" -> fractional hours between two times.
const rowHours = (timeIn, timeOut) => {
    if (!timeIn || !timeOut) return 0;
    const [inH, inM] = timeIn.split(":").map(Number);
    const [outH, outM] = timeOut.split(":").map(Number);
    const diff = (outH * 60 + outM) - (inH * 60 + inM);
    return diff > 0 ? diff / 60 : 0;
};

const round2 = (n) => Math.round(n * 100) / 100;

// Normalizes a time value coming from the API (TimeOnly serializes as
// "HH:mm:ss") down to the "HH:mm" an <input type="time"> expects.
const toInputTime = (val) => {
    if (!val) return "";
    return val.length >= 5 ? val.substring(0, 5) : val;
};

const isLight = (hex) => {
    if (!hex) return false;
    const h = hex.replace('#', '');
    if (h.length !== 6) return false;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
};

// -- Component

const TimeReview = () => {
    const [selectedDate, setSelectedDate] = useState(new Date());

    const [crewOptions, setCrewOptions] = useState([]);
    const [selectedCrew, setSelectedCrew] = useState(null);
    const [nameQuery, setNameQuery] = useState("");

    const [page, setPage] = useState(null);     // TimeReviewPageDTO
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);  // entry id currently saving
    const [approving, setApproving] = useState(false);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    // -- Auth header helper (matches the other components)
    const authHeaders = () => {
        const token = Session.getToken();
        return token
            ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
            : { 'Content-Type': 'application/json' };
    };

    // -- Date navigation
    const handlePrev = () => setSelectedDate((d) => shiftDate(d, -1));
    const handleNext = () => setSelectedDate((d) => shiftDate(d, 1));
    const isToday = selectedDate.toDateString() === new Date().toDateString();

    // -- Load crews for the dropdown (active only)
    useEffect(() => {
        const loadCrews = async () => {
            try {
                const res = await fetch('api/Crew/GetAll?activeOnly=true', {
                    headers: authHeaders(),
                });
                if (!res.ok) throw new Error('Failed to load crews.');
                const crews = await res.json();
                setCrewOptions(
                    crews.map((c) => ({ value: c.id, label: c.name, color: c.colorHex }))
                );
            } catch (e) {
                setError(e.message || 'Failed to load crews.');
            }
        };
        loadCrews();
    }, []);

    // -- Load the review page whenever crew or date changes
    const loadReview = useCallback(async (crewId, date) => {
        setLoading(true);
        setError("");
        setSuccessMsg("");
        try {
            const params = new URLSearchParams({
                crewId,
                date: toLocalDateString(date),
            });
            const res = await fetch(`api/TimeReview?${params}`, {
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Failed to load time review.');
            const data = await res.json();
            setPage(data);
        } catch (e) {
            setError(e.message || 'Failed to load time review.');
            setPage(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!selectedCrew) {
            setPage(null);
            return;
        }
        loadReview(selectedCrew.value, selectedDate);
    }, [selectedCrew, selectedDate, loadReview]);

    // -- Comp-code options shaped for react-select
    const compCodeOptions = useMemo(() => {
        if (!page) return [];
        return page.compCodeOptions.map((c) => ({
            value: c.id,
            label: `${c.code} - ${c.description}`,
        }));
    }, [page]);

    // -- Derived: is there anything still unapproved? (drives the button)
    const hasUnapproved = useMemo(() => {
        if (!page) return false;
        return page.employees.some((emp) => emp.entries.some((e) => !e.approved));
    }, [page]);

    const hasAnyEntries = useMemo(
        () => !!page && page.employees.some((emp) => emp.entries.length > 0),
        [page]
    );

    // -- Name filter (client-side over the loaded crew)
    const visibleEmployees = useMemo(() => {
        if (!page) return [];
        const q = nameQuery.trim().toLowerCase();
        if (!q) return page.employees;
        return page.employees.filter((e) =>
            (e.fullName || "").toLowerCase().includes(q)
        );
    }, [page, nameQuery]);

    // -- Local edit of a single entry's field (optimistic, in-memory only)
    const patchEntryLocal = (entryId, field, value) => {
        setPage((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                employees: prev.employees.map((emp) => ({
                    ...emp,
                    entries: emp.entries.map((en) =>
                        en.id === entryId ? { ...en, [field]: value } : en
                    ),
                })),
            };
        });
    };

    // -- Persist a single entry (per-row save)
    const saveEntry = async (entry) => {
        setSavingId(entry.id);
        setError("");
        try {
            const payload = {
                id: entry.id,
                // API expects "HH:mm:ss"-ish; "HH:mm" parses fine for TimeOnly.
                timeIn: entry.timeIn || null,
                timeOut: entry.timeOut || null,
                workersCompCodeId: entry.workersCompCodeId ?? null,
            };
            const res = await fetch('api/TimeReview/AdjustEntry', {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify(payload),
            });

            if (res.status === 409) {
                // Already approved - reload to resync the locked state.
                setError("That entry was already approved and can no longer be edited.");
                await loadReview(selectedCrew.value, selectedDate);
                return;
            }
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to save the entry.');
            }
        } catch (e) {
            setError(e.message || 'Failed to save the entry.');
        } finally {
            setSavingId(null);
        }
    };

    // -- Approve the whole crew/day
    const handleApprove = async () => {
        if (!selectedCrew) return;
        setApproving(true);
        setError("");
        setSuccessMsg("");
        try {
            const payload = {
                crewId: selectedCrew.value,
                date: toLocalDateString(selectedDate),
            };
            const res = await fetch('api/TimeReview/Approve', {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Failed to approve time.');
            // The endpoint returns the refreshed page already in its locked state.
            const refreshed = await res.json();
            setPage(refreshed);
            setSuccessMsg('Time approved for this crew.');
        } catch (e) {
            setError(e.message || 'Failed to approve time.');
        } finally {
            setApproving(false);
        }
    };

    const selectStyles = {
        dropdownIndicator: (base) => ({
            ...base,
            color: "#272727ff",
            "&:hover": { color: "#272727ff" },
        }),
        menuPortal: (base) => ({ ...base }),
    };

    return (
        <Container className="container-main">
            {/* Alerts */}
            {error && (
                <Row>
                    <Col>
                        <div className="alert alert-danger alert-dismissible mb-3" role="alert">
                            {error}
                            <button type="button" className="btn-close"
                                onClick={() => setError("")} aria-label="Close" />
                        </div>
                    </Col>
                </Row>
            )}
            {successMsg && (
                <Row>
                    <Col>
                        <div className="alert alert-success alert-dismissible mb-3" role="alert">
                            {successMsg}
                            <button type="button" className="btn-close"
                                onClick={() => setSuccessMsg("")} aria-label="Close" />
                        </div>
                    </Col>
                </Row>
            )}

            {/* Controls: crew + name search on the left, date nav center */}
            <Row className="align-items-end" style={{ margin: "1.25rem 0", position: "sticky", top: 0, zIndex: 1020, background: "white", paddingBottom: "2.5rem" }}>
                <Col md="2">
                    <Label style={{ fontSize: "0.85rem" }}>Crew</Label>
                    <Select
                        styles={selectStyles}
                        placeholder="Select a crew..."
                        options={crewOptions}
                        value={selectedCrew}
                        onChange={(val) => setSelectedCrew(val || null)}
                        isClearable
                    />
                </Col>

                <Col md="4">
                    <Label style={{ fontSize: "0.85rem" }}>Search crew member</Label>
                    <Input
                        type="text"
                        placeholder="Type a name..."
                        value={nameQuery}
                        onChange={(e) => setNameQuery(e.target.value)}
                        disabled={!page}
                    />
                </Col>

                <Col xs="12" md="6" className="mt-3 mt-md-0">
                    <div
                        className="justify-content-center justify-content-md-end"
                        style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1.25rem",
                    }}>
                        <Button color="primary" size="md" onClick={handlePrev}>
                            <BsChevronLeft />
                        </Button>
                        <div style={{ textAlign: "center", minWidth: "260px" }}>
                            <h4 style={{ margin: 0 }}>{formatDayLabel(selectedDate)}</h4>
                            {!isToday && (
                                <Button color="link" size="sm"
                                    onClick={() => setSelectedDate(new Date())}
                                    style={{ fontSize: "0.8rem", padding: 0 }}>
                                    Back to today
                                </Button>
                            )}
                        </div>
                        <Button color="primary" size="md" onClick={handleNext}>
                            <BsChevronRight />
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Body */}
            {!selectedCrew ? (
                <div style={{ textAlign: "center", color: "#9ca3af", padding: "3rem 1rem" }}>
                    <p>Select a crew to review time.</p>
                </div>
            ) : loading ? (
                <div style={{ textAlign: "center", marginTop: "3rem" }}>
                    <Spinner />
                </div>
            ) : !hasAnyEntries ? (
                <div style={{ textAlign: "center", color: "#9ca3af", padding: "3rem 1rem" }}>
                    <p>No time entries for this crew today.</p>
                </div>
            ) : (
                <>
                    {visibleEmployees.map((emp) => (
                        <EmployeeBlock
                            key={emp.userId}
                            employee={emp}
                            crewColor={page.crewColorHex}
                            compCodeOptions={compCodeOptions}
                            selectStyles={selectStyles}
                            savingId={savingId}
                            onPatch={patchEntryLocal}
                            onSave={saveEntry}
                        />
                    ))}

                    {/* Approve / Approved */}
                    <div style={{ textAlign: "center", margin: "2rem 0 3rem" }}>
                        {hasUnapproved ? (
                            <Button color="primary" size="md"
                                onClick={handleApprove} disabled={approving}>
                                {approving ? <Spinner size="sm" /> : "Approve"}
                            </Button>
                        ) : (
                            <Button color="success" size="md" disabled>
                                Approved
                            </Button>
                        )}
                    </div>
                </>
            )}
        </Container>
    );
};

// -- One employee's block: name header + their rows + subtotal

function EmployeeBlock({
    employee, crewColor, compCodeOptions, selectStyles, savingId, onPatch, onSave,
}) {
    const accent = crewColor || '#3788d8';
    const headerText = isLight(accent) ? '#1a1a1a' : '#ffffff';

    // Live subtotal recomputed from current (possibly edited) row values.
    const liveTotal = round2(
        employee.entries.reduce(
            (sum, e) => sum + rowHours(toInputTime(e.timeIn), toInputTime(e.timeOut)),
            0
        )
    );

    return (
        <div style={{ marginBottom: "1.75rem" }}>
            <div style={{
                backgroundColor: accent,
                borderRadius: "8px 8px 0 0",
                padding: "0.5rem 1rem",
            }}>
                <span style={{ fontWeight: 700, color: headerText }}>
                    {employee.fullName}
                </span>
            </div>

            <div style={{
                border: "1px solid #e5e7eb",
                borderTop: "none",
                borderRadius: "0 0 8px 8px",
                overflow: "hidden",
            }}>
                {/* Column header row */}
                <Row className="g-0 align-items-center"
                    style={{
                        padding: "0.4rem 1rem",
                        backgroundColor: "#f9fafb",
                        borderBottom: "1px solid #f3f4f6",
                        fontSize: "0.75rem",
                        color: "#6b7280",
                        fontWeight: 600,
                    }}>
                    <Col md="5">Job / Activity</Col>
                    <Col md="3">Time</Col>
                    <Col md="2">Worker's Comp</Col>
                    <Col md="2" className="text-end">Hours</Col>
                </Row>

                {employee.entries.map((entry) => (
                    <EntryRow
                        key={entry.id}
                        entry={entry}
                        compCodeOptions={compCodeOptions}
                        selectStyles={selectStyles}
                        saving={savingId === entry.id}
                        onPatch={onPatch}
                        onSave={onSave}
                    />
                ))}

                {/* Subtotal */}
                <Row className="g-0"
                    style={{
                        padding: "0.45rem 1rem",
                        borderTop: "1px solid #e5e7eb",
                        backgroundColor: "#ffffff",
                    }}>
                    <Col className="text-end" style={{ fontWeight: 700 }}>
                        Total Hours: {liveTotal}
                    </Col>
                </Row>
            </div>
        </div>
    );
}

// -- A single time entry row

function EntryRow({ entry, compCodeOptions, selectStyles, saving, onPatch, onSave }) {
    const locked = entry.approved;

    const timeIn = toInputTime(entry.timeIn);
    const timeOut = toInputTime(entry.timeOut);
    const hours = round2(rowHours(timeIn, timeOut));

    const selectedComp = compCodeOptions.find(
        (o) => o.value === entry.workersCompCodeId
    ) || null;

    return (
        <Row className="g-0 align-items-center"
            style={{
                padding: "0.5rem 1rem",
                borderBottom: "1px solid #f3f4f6",
                backgroundColor: locked ? "#fafafa" : "#ffffff",
            }}>
            {/* Job / Activity */}
            <Col md="5" style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                {entry.jobLabel}
                {locked && (
                    <span style={{
                        fontSize: "0.7rem", color: "#9ca3af", marginLeft: "0.5rem",
                    }}>
                        (approved)
                    </span>
                )}
            </Col>

            {/* Time in / out */}
            <Col md="3">
                <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                    <Input
                        type="time"
                        bsSize="sm"
                        value={timeIn}
                        disabled={locked || saving}
                        onChange={(e) => onPatch(entry.id, "timeIn", e.target.value)}
                        onBlur={() => onSave(entry)}
                        style={{ maxWidth: "110px" }}
                    />
                    <span style={{ color: "#9ca3af" }}>–</span>
                    <Input
                        type="time"
                        bsSize="sm"
                        value={timeOut}
                        disabled={locked || saving}
                        onChange={(e) => onPatch(entry.id, "timeOut", e.target.value)}
                        onBlur={() => onSave(entry)}
                        style={{ maxWidth: "110px" }}
                    />
                </div>
            </Col>

            {/* Worker's Comp */}
            <Col md="2" className="mt-2 mt-md-0" style={{ paddingRight: "0.5rem" }}>
                <Select
                    styles={selectStyles}
                    placeholder="Worker's Comp"
                    options={compCodeOptions}
                    value={selectedComp}
                    isDisabled={locked || saving}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onChange={(opt) => {
                        const newId = opt ? opt.value : null;
                        onPatch(entry.id, "workersCompCodeId", newId);
                        // Save immediately on comp-code change (no blur event on select).
                        onSave({ ...entry, workersCompCodeId: newId });
                    }}
                />
            </Col>

            {/* Hours */}
            <Col md="2" className="text-end" style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                {saving ? <Spinner size="sm" /> : hours}
            </Col>
        </Row>
    );
}

export default TimeReview;